import React, { useEffect, useState } from 'react';
import { Smartphone, Monitor, ChevronRight, CheckCircle, Download, Share2, Plus, MoreVertical } from 'lucide-react';
import Footer from '@/components/Footer';

function Step({ number, icon: Icon, title, desc, highlight }) {
  return (
    <div className="flex gap-4">
      <div className="flex-shrink-0 flex flex-col items-center">
        <div className="w-9 h-9 rounded-full bg-[#0b1e44] text-white flex items-center justify-center font-bold text-sm">{number}</div>
        <div className="w-px flex-1 bg-slate-200 mt-2" />
      </div>
      <div className="pb-8">
        <div className="flex items-center gap-2 mb-1">
          {Icon && <Icon className="h-4 w-4 text-slate-500" />}
          <p className="font-semibold text-slate-900 text-sm">{title}</p>
        </div>
        <p className="text-sm text-slate-600 leading-relaxed">{desc}</p>
        {highlight && (
          <span className="inline-block mt-2 bg-[#0b1e44]/8 text-[#0b1e44] text-xs font-semibold px-3 py-1 rounded-full border border-[#0b1e44]/20">
            {highlight}
          </span>
        )}
      </div>
    </div>
  );
}

function OsCard({ active, label, icon: Icon, onClick }) {
  return (
    <button
      onClick={onClick}
      data-testid={`os-tab-${label.toLowerCase()}`}
      className={`flex-1 flex items-center justify-center gap-2 py-3 px-4 rounded-xl font-semibold text-sm transition-all ${
        active
          ? 'bg-[#0b1e44] text-white shadow-md'
          : 'bg-white text-slate-600 border border-slate-200 hover:border-slate-400'
      }`}
    >
      <Icon className="h-4 w-4" />
      {label}
    </button>
  );
}

export default function InstalarPWA() {
  const [os, setOs] = useState('android');

  useEffect(() => {
    const ios = /iphone|ipad|ipod/i.test(navigator.userAgent);
    if (ios) setOs('ios');
    document.title = 'Instalar Trabadju · App Gratuita para Telemóvel';
  }, []);

  const [installed, setInstalled] = useState(false);
  const [deferredPrompt, setDeferredPrompt] = useState(null);

  useEffect(() => {
    if (window.matchMedia('(display-mode: standalone)').matches || window.navigator.standalone) {
      setInstalled(true);
    }
    const handler = (e) => { e.preventDefault(); setDeferredPrompt(e); };
    window.addEventListener('beforeinstallprompt', handler);
    return () => window.removeEventListener('beforeinstallprompt', handler);
  }, []);

  const handleInstall = async () => {
    if (!deferredPrompt) return;
    deferredPrompt.prompt();
    const { outcome } = await deferredPrompt.userChoice;
    if (outcome === 'accepted') setInstalled(true);
    setDeferredPrompt(null);
  };

  return (
    <main className="min-h-screen bg-[#f8fafc]" data-testid="instalar-page">
      {/* Hero */}
      <section className="bg-[#0b1e44] text-white">
        <div className="max-w-3xl mx-auto px-5 py-14 sm:py-20 text-center">
          <div className="inline-flex items-center gap-2 bg-white/10 text-white/90 text-xs font-semibold px-3 py-1.5 rounded-full mb-6">
            <Smartphone className="h-3.5 w-3.5" />
            Aplicação Gratuita
          </div>
          <h1 className="text-3xl sm:text-4xl lg:text-5xl font-bold tracking-tight leading-tight mb-4">
            Instale o Trabadju<br />no seu telemóvel
          </h1>
          <p className="text-white/70 text-base sm:text-lg max-w-xl mx-auto leading-relaxed">
            Acesso rápido, notificações de vagas e candidaturas — mesmo sem abrir o browser.
            Grátis, sem App Store, instala em segundos.
          </p>

          {installed ? (
            <div className="mt-8 inline-flex items-center gap-2 bg-green-500/20 border border-green-500/30 text-green-300 px-5 py-3 rounded-xl font-semibold" data-testid="already-installed">
              <CheckCircle className="h-5 w-5" />
              Aplicação já instalada!
            </div>
          ) : deferredPrompt ? (
            <button
              onClick={handleInstall}
              data-testid="install-btn-hero"
              className="mt-8 inline-flex items-center gap-2 bg-white text-[#0b1e44] font-bold px-6 py-3 rounded-xl hover:bg-white/90 transition-colors shadow-lg"
            >
              <Download className="h-5 w-5" />
              Instalar agora (Android)
            </button>
          ) : (
            <p className="mt-8 text-white/50 text-sm">Siga as instruções abaixo para o seu dispositivo</p>
          )}
        </div>
      </section>

      {/* Benefits */}
      <section className="max-w-3xl mx-auto px-5 py-10">
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
          {[
            { icon: '⚡', title: 'Acesso rápido', desc: 'Ícone no ecrã principal como qualquer outra app' },
            { icon: '📶', title: 'Funciona offline', desc: 'Consulte vagas guardadas mesmo sem internet' },
            { icon: '🔔', title: 'Notificações', desc: 'Alertas de novas vagas e respostas a candidaturas' },
          ].map((b) => (
            <div key={b.title} className="bg-white border border-slate-200 rounded-2xl p-5 text-center">
              <div className="text-3xl mb-3">{b.icon}</div>
              <p className="font-bold text-slate-900 text-sm mb-1">{b.title}</p>
              <p className="text-xs text-slate-500 leading-relaxed">{b.desc}</p>
            </div>
          ))}
        </div>
      </section>

      {/* Instructions */}
      <section className="max-w-3xl mx-auto px-5 pb-16">
        <h2 className="text-xl font-bold text-slate-900 mb-6 text-center">Como instalar</h2>

        {/* OS tabs */}
        <div className="flex gap-3 mb-8" data-testid="os-tabs">
          <OsCard active={os === 'android'} label="Android" icon={Smartphone} onClick={() => setOs('android')} />
          <OsCard active={os === 'ios'} label="iPhone / iPad" icon={Monitor} onClick={() => setOs('ios')} />
        </div>

        <div className="bg-white border border-slate-200 rounded-2xl p-6 sm:p-8" data-testid="instructions-panel">
          {os === 'android' ? (
            <>
              <div className="flex items-center gap-2 mb-6">
                <div className="w-8 h-8 rounded-lg bg-green-100 flex items-center justify-center">
                  <Smartphone className="h-4 w-4 text-green-700" />
                </div>
                <div>
                  <p className="font-bold text-slate-900 text-sm">Android · Google Chrome</p>
                  <p className="text-xs text-slate-500">Funciona no Chrome, Edge e Samsung Browser</p>
                </div>
              </div>

              <Step number="1" icon={null} title="Abra trabadju.cv no Chrome"
                desc="Certifique-se de que está a usar o Google Chrome. Outros browsers podem não suportar a instalação." />
              <Step number="2" icon={MoreVertical} title='Toque nos três pontos "⋮" no canto superior direito'
                desc="O menu de opções do Chrome aparece no canto superior direito do ecrã." />
              <Step number="3" icon={Download} title='Seleccione "Instalar aplicação" ou "Adicionar ao ecrã inicial"'
                desc='Uma janela de confirmação irá aparecer. Toque em "Instalar" para confirmar.'
                highlight='Instalar aplicação' />
              <Step number="4" icon={CheckCircle} title="Pronto! Ícone adicionado ao ecrã"
                desc="O Trabadju aparece no seu ecrã principal como qualquer outra aplicação. Abre directamente, sem browser." />

              {deferredPrompt && (
                <button
                  onClick={handleInstall}
                  data-testid="install-btn-android"
                  className="mt-2 w-full flex items-center justify-center gap-2 bg-[#0b1e44] text-white font-semibold py-3 rounded-xl hover:bg-[#0b1e44]/90 transition-colors"
                >
                  <Download className="h-4 w-4" />
                  Instalar automaticamente
                </button>
              )}
            </>
          ) : (
            <>
              <div className="flex items-center gap-2 mb-6">
                <div className="w-8 h-8 rounded-lg bg-blue-100 flex items-center justify-center">
                  <Monitor className="h-4 w-4 text-blue-700" />
                </div>
                <div>
                  <p className="font-bold text-slate-900 text-sm">iPhone / iPad · Safari</p>
                  <p className="text-xs text-slate-500">Tem de usar o Safari — outros browsers no iOS não suportam</p>
                </div>
              </div>

              <Step number="1" icon={null} title="Abra trabadju.cv no Safari"
                desc="No iPhone ou iPad, abra o Safari (o browser nativo da Apple). Chrome ou Firefox no iOS não suportam instalação de PWA."
                highlight="Obrigatório usar o Safari" />
              <Step number="2" icon={Share2} title='Toque no botão Partilhar'
                desc='O botão Partilhar é o ícone com uma seta para cima (□↑), na barra inferior do Safari. Em iPad, fica na barra de endereço.' />
              <Step number="3" icon={Plus} title='"Adicionar ao ecrã principal"'
                desc='Deslize o menu de partilha para baixo e toque em "Adicionar ao ecrã principal" (ícone com +). Pode editar o nome se quiser.'
                highlight='Adicionar ao ecrã principal' />
              <Step number="4" icon={ChevronRight} title='Toque em "Adicionar" no canto superior direito'
                desc='O Trabadju é adicionado ao seu ecrã principal. A próxima vez que abrir, aparece como uma app a ecrã completo, sem a barra do Safari.' />
              <Step number="5" icon={CheckCircle} title="Instalado!"
                desc="O ícone do Trabadju aparece no ecrã principal. Abre directamente, como qualquer outra app. Pode movê-lo para onde quiser." />
            </>
          )}
        </div>

        {/* FAQ */}
        <div className="mt-8 space-y-4">
          <h3 className="font-bold text-slate-900 text-base">Perguntas frequentes</h3>
          {[
            {
              q: 'É gratuito?',
              a: 'Completamente. Não é necessário cartão de crédito nem conta na App Store.'
            },
            {
              q: 'Ocupa muito espaço no telemóvel?',
              a: 'Menos de 1 MB. A aplicação carrega o conteúdo da internet conforme necessário.'
            },
            {
              q: 'Posso desinstalar?',
              a: 'Sim. Prima e segure o ícone, como faria com qualquer outra app, e seleccione "Remover" ou "Desinstalar".'
            },
            {
              q: 'Funciona sem internet?',
              a: 'Sim, pode consultar as últimas vagas guardadas em modo offline. Para ver novas vagas e candidatar-se, precisa de ligação.'
            },
          ].map(({ q, a }) => (
            <div key={q} className="bg-white border border-slate-200 rounded-xl p-4">
              <p className="font-semibold text-slate-900 text-sm mb-1">{q}</p>
              <p className="text-sm text-slate-600">{a}</p>
            </div>
          ))}
        </div>
      </section>

      <Footer />
    </main>
  );
}
